import { fmtINR } from '../../lib/currency';
import { getWorkspaceId } from '../../lib/auth-storage';
import { useCallback, useEffect, useState } from 'react';
import {
  analyticsApi,
  type ActivityEntry,
  type AIPerformanceSummary,
  type AttributionChain,
  type ExecutiveDashboard,
  type Experiment,
  type IntelligenceNotification,
  type IntelligenceRecommendation,
  type LearningSignal,
} from '../../api/analytics';

const WORKSPACE = (getWorkspaceId() ?? '');

export function DecisionIntelligenceDashboardPage() {
  const [executive, setExecutive] = useState<ExecutiveDashboard | null>(null);
  const [aiPerf, setAiPerf] = useState<AIPerformanceSummary | null>(null);
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [recommendations, setRecommendations] = useState<IntelligenceRecommendation[]>([]);
  const [signals, setSignals] = useState<LearningSignal[]>([]);
  const [attributions, setAttributions] = useState<AttributionChain[]>([]);
  const [notifications, setNotifications] = useState<IntelligenceNotification[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [tab, setTab] = useState<'executive' | 'ai' | 'experiments' | 'recommendations'>('executive');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [exec, ai, exps, recs, sigs, attrs, notifs, act] = await Promise.all([
        analyticsApi.executiveDashboard(WORKSPACE),
        analyticsApi.aiDashboard(WORKSPACE),
        analyticsApi.listExperiments(WORKSPACE),
        analyticsApi.listRecommendations(WORKSPACE),
        analyticsApi.listSignals(WORKSPACE),
        analyticsApi.listAttributions(WORKSPACE),
        analyticsApi.listNotifications(true),
        analyticsApi.listActivity(15),
      ]);
      setExecutive(exec);
      setAiPerf(ai);
      setExperiments(exps);
      setRecommendations(recs);
      setSignals(sigs);
      setAttributions(attrs);
      setNotifications(notifs);
      setActivity(act);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load decision intelligence dashboard');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createSampleExperiment = async () => {
    setBusy(true);
    try {
      const exp = await analyticsApi.createExperiment({
        workspace_id: WORKSPACE,
        name: 'Title A/B Test — Widget Set',
        type: 'title',
        variant_a: 'Acme Premium Widget Tool Set',
        variant_b: 'Acme Pro Widget Kit — Professional Grade',
        asin: 'B000DEMO01',
      });
      await analyticsApi.startExperiment(exp.id);
      await analyticsApi.captureSignal({
        workspace_id: WORKSPACE,
        type: 'accepted',
        domain: 'ai_listing',
        prompt_version: 'v3.2',
        rating: 0.88,
        outcome: 'approved',
      });
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const evaluateExperiment = async (id: string) => {
    await analyticsApi.evaluateExperiment(id);
    refresh();
  };

  const formatCurrency = (n: number) => fmtINR(n);

  const formatPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Decision Intelligence</h1>
          <p className="text-sm text-slate-500">
            Measure, correlate, explain, predict &amp; learn — FCP-00008
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={createSampleExperiment}
            className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
          >
            Seed Experiment + Signal
          </button>
          <button type="button" onClick={refresh} className="rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex gap-2 border-b border-slate-200">
        {(['executive', 'ai', 'experiments', 'recommendations'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize ${
              tab === t ? 'border-b-2 border-violet-600 text-violet-600' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t === 'ai' ? 'AI Performance' : t === 'experiments' ? 'Experiment Center' : t === 'recommendations' ? 'Recommendations' : 'Executive'}
          </button>
        ))}
      </div>

      {tab === 'executive' && executive && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Revenue', value: formatCurrency(executive.revenue), sub: formatPct(executive.revenue_growth) },
              { label: 'Orders', value: executive.orders.toLocaleString(), sub: formatPct(executive.order_growth) },
              { label: 'Conversion', value: `${(executive.conversion_rate * 100).toFixed(2)}%` },
              { label: 'AOV', value: formatCurrency(executive.average_order_value) },
              { label: 'Catalog Health', value: `${(executive.catalog_health * 100).toFixed(0)}%` },
              { label: 'Inventory Health', value: `${(executive.inventory_health * 100).toFixed(0)}%` },
              { label: 'Publishing Success', value: `${(executive.publishing_success * 100).toFixed(0)}%` },
              { label: 'AI Readiness', value: `${(executive.ai_readiness_score * 100).toFixed(0)}%` },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
                {kpi.sub && <p className="text-xs text-green-600">{kpi.sub} vs prior</p>}
              </div>
            ))}
          </div>

          {attributions.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="font-medium text-slate-900">Attribution (correlation, not causation)</h2>
              <div className="mt-3 space-y-2">
                {attributions.slice(0, 3).map((a) => (
                  <div key={a.id} className="rounded-lg bg-slate-50 p-3 text-sm">
                    <p className="font-medium">{a.published_change}</p>
                    <p className="text-slate-600">{a.explanation}</p>
                    <p className="mt-1 text-xs text-slate-500">Confidence {(a.confidence * 100).toFixed(0)}%</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'ai' && aiPerf && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { label: 'Prompt Acceptance', value: `${(aiPerf.prompt_acceptance_rate * 100).toFixed(0)}%` },
            { label: 'Prompt Rejection', value: `${(aiPerf.prompt_rejection_rate * 100).toFixed(0)}%` },
            { label: 'Avg Confidence', value: `${(aiPerf.avg_confidence * 100).toFixed(0)}%` },
            { label: 'Generation Latency', value: `${aiPerf.avg_generation_latency_ms.toFixed(0)}ms` },
            { label: 'Provider Cost', value: formatCurrency(aiPerf.provider_cost) },
            { label: 'Human Edit Rate', value: `${(aiPerf.human_edit_rate * 100).toFixed(0)}%` },
            { label: 'Rollback Rate', value: `${(aiPerf.rollback_rate * 100).toFixed(1)}%` },
            { label: 'Rec Acceptance', value: `${(aiPerf.recommendation_acceptance * 100).toFixed(0)}%` },
            { label: 'Publishing Success', value: `${(aiPerf.publishing_success_rate * 100).toFixed(0)}%` },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-xl font-semibold text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'experiments' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="font-medium text-slate-900">A/B Experiments</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {experiments.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-500">No experiments — seed one to get started</p>
            ) : (
              experiments.map((exp) => (
                <div key={exp.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="font-medium text-slate-900">{exp.name}</p>
                    <p className="text-xs text-slate-500">
                      {exp.type} · {exp.status}
                      {exp.winner && ` · Winner: ${exp.winner}`}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">A: {exp.variant_a.slice(0, 40)}…</p>
                  </div>
                  {exp.status === 'running' && (
                    <button
                      type="button"
                      onClick={() => evaluateExperiment(exp.id)}
                      className="rounded-lg bg-violet-600 px-3 py-1 text-xs text-white hover:bg-violet-700"
                    >
                      Evaluate
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3">
              <h2 className="font-medium text-slate-900">Governed Recommendations</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {recommendations.map((rec) => (
                <div key={rec.id} className="px-4 py-3">
                  <div className="flex items-start justify-between">
                    <p className="font-medium text-slate-900">{rec.title}</p>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        rec.priority === 'critical' ? 'bg-red-100 text-red-800' : rec.priority === 'high' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {rec.priority ?? 'normal'}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{rec.description}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Confidence {(rec.confidence * 100).toFixed(0)}%
                    {rec.impact != null && ` · Impact +${(rec.impact * 100).toFixed(0)}%`}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">Learning Signals ({signals.length})</h3>
              <div className="mt-2 space-y-1">
                {signals.slice(0, 6).map((s) => (
                  <div key={s.id} className="text-xs text-slate-600">
                    <span className="font-medium">{s.type}</span> · {s.domain}
                    {s.prompt_version && ` · ${s.prompt_version}`}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">Notifications</h3>
              <div className="mt-2 space-y-2">
                {notifications.length === 0 ? (
                  <p className="text-xs text-slate-500">No unread notifications</p>
                ) : (
                  notifications.slice(0, 4).map((n) => (
                    <div key={n.id} className="rounded-lg bg-slate-50 p-2 text-xs">
                      <p className="font-medium">{n.title}</p>
                      <p className="text-slate-500">{n.body}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">Recent Activity</h3>
              <div className="mt-2 space-y-1">
                {activity.slice(0, 6).map((a) => (
                  <div key={a.id} className="text-xs text-slate-600">
                    {a.action} — {a.message}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
